import { useEffect, useMemo, useState } from "react";
import { sortAges, compareTeams } from "@/lib/ageOrder";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Pencil, X, UserCog, AlertTriangle, Wrench, GitMerge } from "lucide-react";
import ImageUpload, { StorageImage } from "@/components/app/ImageUpload";
import { UserPicker } from "@/components/app/UserPicker";
import { FtAdminDialog, MergeTeamDialog } from "@/components/teams/TeamExtraDialogs";

const AGES = ["U6","U7","U8","U9","U10","U11","U12","U13","U14","U15","U16","U17","U18","Adults"];
const FORMATS = ["3v3", "5v5", "7v7", "9v9", "11v11"];
const COLORS = [
  { name: "Black", hex: "#0F172A" },
  { name: "White", hex: "#F8FAFC" },
  { name: "Green", hex: "#16A34A" },
  { name: "Yellow", hex: "#83B68B" },
  { name: "Blue", hex: "#2563EB" },
];
const DEFAULT_LABELS = { Black: "Black", White: "White", Green: "Green", Yellow: "Yellow", Blue: "Blue" };
const colorHex = (name) => COLORS.find(c => c.name === name)?.hex || "#83B68B";

const emptyForm = { name: "", age_group: "U9", coach_name: "", squad: "", game_format: "7v7", gender: "", fa_results_url: "", fa_table_url: "", color: "Yellow", league_id: "", owner_id: "", season_id: "", default_venue_id: "", default_pitch_id: "", coach_ids: [], ft_admin_first_name: "", ft_admin_surname: "", ft_admin_phone: "", ft_admin_email: "" };
const GENDERS = [["male", "Male"], ["female", "Female"], ["mixed", "Mixed"]];
export const genderLabel = (g) => (GENDERS.find(x => x[0] === g) || [])[1] || "";

export default function Teams() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [teams, setTeams] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [users, setUsers] = useState([]);
  const [kitLabels, setKitLabels] = useState(DEFAULT_LABELS);
  const [openTeam, setOpenTeam] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [ftTeam, setFtTeam] = useState(null);
  const [mergeSource, setMergeSource] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [filterAge, setFilterAge] = useState("all");
  const [filterLeague, setFilterLeague] = useState("all");
  // Search + typeahead for the "Additional coaches" picker on the team form.
  const [coachSearch, setCoachSearch] = useState("");
  const [coachSearchOpen, setCoachSearchOpen] = useState(false);
  const [filterCoach, setFilterCoach] = useState("all");
  const [filterSquad, setFilterSquad] = useState("all");
  const [filterMismatch, setFilterMismatch] = useState(false);
  const [faRules, setFaRules] = useState([]);
  const [venues, setVenues] = useState([]);
  const [pitches, setPitches] = useState([]);
  const venueById = useMemo(() => Object.fromEntries(venues.map(v => [v.id, v])), [venues]);
  const pitchesForVenue = useMemo(() => (
    form.default_venue_id ? pitches.filter(p => p.venue_id === form.default_venue_id && p.active) : []
  ), [pitches, form.default_venue_id]);
  const rulesByAge = useMemo(() => {
    const m = {};
    for (const r of faRules) m[(r.age_group || "").toUpperCase()] = r;
    return m;
  }, [faRules]);
  const kitName = (c) => (c && kitLabels[c]) || c || "";

  const ruleForTeam = (t) => rulesByAge[(t.age_group || "").toUpperCase()];
  const isMismatch = (t) => {
    const r = ruleForTeam(t);
    return !!(r && t.game_format && r.game_format && t.game_format !== r.game_format);
  };
  const mismatchCount = useMemo(() => teams.filter(isMismatch).length, [teams, rulesByAge]);

  const filteredTeams = useMemo(() => teams.filter(t =>
    (filterAge === "all" || t.age_group === filterAge) &&
    (filterLeague === "all" || (filterLeague === "none" ? !t.league_id : t.league_id === filterLeague)) &&
    (filterCoach === "all" || (filterCoach === "none" ? !t.coach_name : (t.coach_name || "") === filterCoach)) &&
    (filterSquad === "all" || (filterSquad === "none" ? !t.squad : (t.squad || "") === filterSquad)) &&
    (!filterMismatch || isMismatch(t))
  ).sort(compareTeams),
  [teams, filterAge, filterLeague, filterCoach, filterSquad, filterMismatch, rulesByAge]);

  const uniqueAges = useMemo(() => sortAges([...new Set(teams.map(t => t.age_group).filter(Boolean))]), [teams]);
  const uniqueCoaches = useMemo(() => [...new Set(teams.map(t => t.coach_name).filter(Boolean))].sort(), [teams]);
  const uniqueSquads = useMemo(() => [...new Set(teams.map(t => t.squad).filter(Boolean))].sort(), [teams]);
  const anyFilter = filterAge !== "all" || filterLeague !== "all" || filterCoach !== "all" || filterSquad !== "all" || filterMismatch;
  const clearFilters = () => { setFilterAge("all"); setFilterLeague("all"); setFilterCoach("all"); setFilterSquad("all"); setFilterMismatch(false); };

  const fixFormat = async (t) => {
    const r = ruleForTeam(t);
    if (!r) return;
    try {
      await api.put(`/teams/${t.id}`, {
        name: t.name, age_group: t.age_group, coach_name: t.coach_name,
        squad: t.squad, game_format: r.game_format, color: t.color || "Yellow",
        league_id: t.league_id || null, owner_id: t.owner_id || null,
        default_venue_id: t.default_venue_id || null,
      });
      toast.success(`${t.name}: ${t.game_format} → ${r.game_format}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const [bulkFixing, setBulkFixing] = useState(false);
  const fixAllMismatches = async () => {
    const rows = teams.filter(isMismatch);
    if (rows.length === 0) return;
    if (!window.confirm(`Fix ${rows.length} team${rows.length === 1 ? "" : "s"} to their Future Fit game format? This cannot be undone.`)) return;
    setBulkFixing(true);
    const results = await Promise.allSettled(rows.map(t => {
      const r = ruleForTeam(t);
      return api.put(`/teams/${t.id}`, {
        name: t.name, age_group: t.age_group, coach_name: t.coach_name,
        squad: t.squad, game_format: r.game_format, color: t.color || "Yellow",
        league_id: t.league_id || null, owner_id: t.owner_id || null,
        default_venue_id: t.default_venue_id || null,
      });
    }));
    const ok = results.filter(r => r.status === "fulfilled").length;
    const failed = results.length - ok;
    if (failed === 0) toast.success(`Fixed ${ok} team${ok === 1 ? "" : "s"} to Future Fit`);
    else toast.error(`Fixed ${ok}/${results.length} — ${failed} failed`);
    setBulkFixing(false);
    load();
  };

  const load = async () => {
    const [t, l, k, fr, v, p] = await Promise.all([
      api.get("/teams"),
      api.get("/leagues"),
      api.get("/settings/kit-labels").catch(()=>({data:{labels:DEFAULT_LABELS}})),
      api.get("/fa/age-rules").catch(()=>({data:{rules:[]}})),
      api.get("/venues").catch(()=>({data:[]})),
      api.get("/pitches").catch(()=>({data:[]})),
    ]);
    setTeams(t.data); setLeagues(l.data);
    setKitLabels({...DEFAULT_LABELS, ...(k.data.labels || {})});
    setFaRules(fr.data.rules || []);
    setVenues(v.data || []);
    setPitches(p.data || []);
    if (isAdmin) {
      try { const u = await api.get("/admin/users"); setUsers(u.data); }
      catch (err) { console.warn("Failed to load users list for team owner picker:", err); }
    }
  };
  useEffect(() => { load(); }, [isAdmin]);

  const reassignOwner = async (teamId, newOwnerId) => {
    try {
      await api.patch(`/teams/${teamId}/owner`, { owner_id: newOwnerId || null });
      toast.success(newOwnerId ? "Team reassigned" : "Team owner cleared");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openCreate = () => { setForm(emptyForm); setEditingTeam(null); setOpenTeam(true); };
  const openEdit = (t) => {
    setEditingTeam(t);
    setForm({
      name: t.name, age_group: t.age_group, coach_name: t.coach_name || "",
      squad: t.squad || "", game_format: t.game_format || "7v7", gender: t.gender || "", fa_results_url: t.fa_results_url || "", fa_table_url: t.fa_table_url || "",
      color: t.color || "Yellow", league_id: t.league_id || "",
      owner_id: t.owner_id || "",
      season_id: t.season_id || "",
      default_venue_id: t.default_venue_id || "",
      default_pitch_id: t.default_pitch_id || "",
      coach_ids: Array.isArray(t.coach_ids) ? [...t.coach_ids] : [],
      ft_admin_first_name: t.ft_admin_first_name || (t.ft_admin_name || "").split(" ")[0] || "", ft_admin_surname: t.ft_admin_surname || (t.ft_admin_name || "").split(" ").slice(1).join(" ") || "", ft_admin_phone: t.ft_admin_phone || "", ft_admin_email: t.ft_admin_email || "",
    });
    setOpenTeam(true);
  };

  const submitTeam = async () => {
    if (!form.name) return toast.error("Team name required");
    if (!editingTeam && !form.owner_id) return toast.error("Lead Coach is required — pick a user who owns this team");
    const payload = {
      name: form.name, age_group: form.age_group,
      coach_name: form.coach_name || null,
      squad: form.squad || null,
      game_format: form.game_format || null,
      gender: form.gender || null,
      fa_results_url: (form.fa_results_url || "").trim() || null, fa_table_url: (form.fa_table_url || "").trim() || null,
      color: form.color || "Yellow",
      league_id: form.league_id || null,
      owner_id: form.owner_id || null,
      season_id: form.season_id || null,
      default_venue_id: form.default_venue_id || null,
      default_pitch_id: form.default_pitch_id || null,
      ft_admin_first_name: form.ft_admin_first_name || null, ft_admin_surname: form.ft_admin_surname || null, ft_admin_phone: form.ft_admin_phone || null, ft_admin_email: form.ft_admin_email || null,
    };
    try {
      if (editingTeam) {
        await api.put(`/teams/${editingTeam.id}`, payload);
        // Additional coaches ride on a separate admin-only endpoint so non-admins can't smuggle them in via team edit.
        if (isAdmin) {
          await api.patch(`/teams/${editingTeam.id}/coaches`, { coach_ids: form.coach_ids || [] });
        }
        toast.success("Team updated");
      }
      else {
        const { data: created } = await api.post("/teams", payload);
        if (isAdmin && (form.coach_ids || []).length > 0) {
          await api.patch(`/teams/${created.id}/coaches`, { coach_ids: form.coach_ids });
        }
        toast.success("Team created");
      }
      setOpenTeam(false); setEditingTeam(null); setForm(emptyForm); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const deleteTeam = async (id) => {
    if (!window.confirm("Delete this team?")) return;
    await api.delete(`/teams/${id}`); toast.success("Team removed"); load();
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B]">Squads</div>
          <h1 className="font-display font-black text-4xl md:text-5xl mt-2 leading-tight">Teams & rosters</h1>
        </div>
        <Dialog open={openTeam} onOpenChange={(v)=>{setOpenTeam(v); if(!v){setEditingTeam(null); setForm(emptyForm);}}}>
          {isAdmin && (
            <DialogTrigger asChild>
              <button onClick={openCreate} className="btn-volt px-5 py-3 rounded-md inline-flex items-center gap-2" data-testid="teams-new-btn"><Plus className="w-4 h-4" /> New team</button>
            </DialogTrigger>
          )}
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">{editingTeam ? "Edit team" : "Create team"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-widest opacity-70">Team name</Label>
                <Input value={form.name} onChange={e=>setForm({...form, name: e.target.value})} data-testid="teams-name-input" className="mt-2" placeholder="U10 Lions" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest opacity-70">Age group</Label>
                <Select value={form.age_group} onValueChange={v=>setForm({...form, age_group: v})}>
                  <SelectTrigger className="mt-2"><SelectValue /></SelectTrigger>
                  <SelectContent>{AGES.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest opacity-70">Squad (optional)</Label>
                <Input value={form.squad} onChange={e=>setForm({...form, squad: e.target.value})} className="mt-2" placeholder="First Team, Reserves…" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest opacity-70">Game format</Label>
                <Select value={form.game_format} onValueChange={v=>setForm({...form, game_format: v})}>
                  <SelectTrigger className="mt-2"><SelectValue /></SelectTrigger>
                  <SelectContent>{FORMATS.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest opacity-70">Gender</Label>
                <Select value={form.gender || "none"} onValueChange={v=>setForm({...form, gender: v === "none" ? "" : v})}>
                  <SelectTrigger className="mt-2" data-testid="team-gender"><SelectValue placeholder="Not set" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Not set</SelectItem>{GENDERS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest opacity-70">Kit colour</Label>
                <div className="mt-2 grid grid-cols-5 gap-1.5">
                  {COLORS.map(c => (
                    <button
                      key={c.name}
                      type="button"
                      title={kitName(c.name)}
                      onClick={()=>setForm({...form, color: c.name})}
                      data-testid={`teams-color-${c.name.toLowerCase()}`}
                      className={`h-9 rounded-md border-2 transition-all ${form.color === c.name ? "border-[#83B68B] scale-105" : "border-border hover:border-white/40"}`}
                      style={{ background: c.hex }}
                    />
                  ))}
                </div>
                <div className="text-[10px] tracking-widest uppercase opacity-60 mt-1" data-testid="teams-color-label">{kitName(form.color)}{kitName(form.color) !== form.color ? ` (${form.color})` : ""}</div>
              </div>
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-widest opacity-70">Default venue (optional)</Label>
                <Select value={form.default_venue_id || "none"} onValueChange={v=>setForm({...form, default_venue_id: v === "none" ? "" : v, default_pitch_id: ""})}>
                  <SelectTrigger data-testid="teams-default-venue-select" className="mt-2"><SelectValue placeholder="No default venue" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No default venue</SelectItem>
                    {venues.map(v => <SelectItem key={v.id} value={v.id}>{v.name}{v.is_unavailable ? " · UNAVAILABLE" : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[10px] opacity-50 mt-1.5">FA Import will suggest a pitch at this venue first before falling back to any pitch matching the game format.</p>
              </div>
              {form.default_venue_id && pitchesForVenue.length > 0 && (
                <div className="col-span-2">
                  <Label className="text-xs uppercase tracking-widest opacity-70">Default pitch (optional)</Label>
                  <Select value={form.default_pitch_id || "none"} onValueChange={v=>setForm({...form, default_pitch_id: v === "none" ? "" : v})}>
                    <SelectTrigger data-testid="teams-default-pitch-select" className="mt-2"><SelectValue placeholder="Any pitch at this venue" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Any pitch at this venue</SelectItem>
                      {pitchesForVenue.map(p => <SelectItem key={p.id} value={p.id}>{p.name}{p.game_format ? ` · ${p.game_format}` : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] opacity-50 mt-1.5">Locks FA imports and calendar suggestions to this exact pitch. Leave blank to use any pitch at the venue that matches the game format.</p>
                </div>
              )}
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-widest opacity-70">League (optional)</Label>
                <Select value={form.league_id || "none"} onValueChange={v=>setForm({...form, league_id: v === "none" ? "" : v})}>
                  <SelectTrigger data-testid="teams-league-select" className="mt-2"><SelectValue placeholder="No league" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No league</SelectItem>
                    {leagues.map(l => <SelectItem key={l.id} value={l.id}>{l.name}{l.division ? ` · ${l.division}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 grid sm:grid-cols-2 gap-2">
                <div className="sm:col-span-2"><Label className="text-xs uppercase tracking-widest opacity-70">FA Full-Time division pages <span className="opacity-50">(this team's results &amp; table — refreshed nightly; published on the website only if the league allows)</span></Label></div>
                <Input placeholder="Results URL — https://fulltime.thefa.com/results.html?…" value={form.fa_results_url} onChange={e=>setForm({...form, fa_results_url: e.target.value})} data-testid="teams-fa-results-url" />
                <Input placeholder="Table URL — https://fulltime.thefa.com/table.html?…" value={form.fa_table_url} onChange={e=>setForm({...form, fa_table_url: e.target.value})} data-testid="teams-fa-table-url" />
                {editingTeam && (form.fa_results_url || form.fa_table_url) && (
                  <button type="button" data-testid="teams-fa-results-refresh" onClick={async () => { try { const { data } = await api.post(`/teams/${editingTeam.id}/fa-results-refresh`); toast.success(`Imported ${data.results} results · ${data.table_rows} table rows${data.errors?.length ? ` · ${data.errors.join("; ")}` : ""}`); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } }}
                    className="sm:col-span-2 justify-self-start text-[10px] tracking-widest uppercase px-3 py-2 border border-[#83B68B] text-[#83B68B] rounded hover:bg-[#83B68B]/10">Import results &amp; table now</button>
                )}
              </div>
              <div className="col-span-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div className="col-span-2 sm:col-span-4"><Label className="text-xs uppercase tracking-widest opacity-70">FA Full-Time administrator <span className="opacity-50">(only if different from the lead coach)</span></Label></div>
                <Input placeholder="First name" value={form.ft_admin_first_name} onChange={e=>setForm({...form, ft_admin_first_name: e.target.value})} data-testid="teams-ft-admin-first" />
                <Input placeholder="Surname" value={form.ft_admin_surname} onChange={e=>setForm({...form, ft_admin_surname: e.target.value})} data-testid="teams-ft-admin-surname" />
                <Input placeholder="Mobile" value={form.ft_admin_phone} onChange={e=>setForm({...form, ft_admin_phone: e.target.value})} data-testid="teams-ft-admin-phone" />
                <Input placeholder="Email" type="email" value={form.ft_admin_email} onChange={e=>setForm({...form, ft_admin_email: e.target.value})} data-testid="teams-ft-admin-email" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-widest opacity-70">Lead Coach {editingTeam ? "" : <span className="text-red-400">*</span>} {isAdmin ? "" : "(admin-managed)"}</Label>
                <UserPicker
                  users={users}
                  value={form.owner_id || null}
                  noneLabel={editingTeam ? "— unchanged —" : undefined}
                  onChange={(v)=>{
                    if (!v) { setForm({...form, owner_id: ""}); return; }
                    const picked = users.find(x => x.id === v);
                    setForm({
                      ...form,
                      owner_id: v,
                      // auto-sync free-text coach_name if empty OR was matching another user
                      coach_name: (!form.coach_name || users.some(x => x.name === form.coach_name)) ? (picked?.name || form.coach_name) : form.coach_name,
                    });
                  }}
                  disabled={!isAdmin}
                  className={`mt-2 ${!editingTeam && !form.owner_id ? "border-red-500/60" : ""}`}
                  testId="teams-lead-coach-select"
                />
                <p className="text-[10px] opacity-50 mt-1.5">Required. The Lead Coach owns the team — they'll see it in their calendar dropdown when booking.</p>
              </div>
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-widest opacity-70">Coach display name {form.owner_id ? "(auto-synced)" : "(free text)"}</Label>
                <Input value={form.coach_name} onChange={e=>setForm({...form, coach_name: e.target.value})} className="mt-2" placeholder="Coach name" />
              </div>
              {isAdmin && (
                <div className="col-span-2" data-testid="teams-additional-coaches">
                  <Label className="text-xs uppercase tracking-widest opacity-70">Additional coaches <span className="opacity-50">(optional)</span></Label>
                  <p className="text-[10px] opacity-50 mt-1.5">Extra coaches who can view, book, amend and cancel slots for this team — in addition to the Lead Coach above.</p>
                  {/* Selected pills — one chip per selected user, click × to remove */}
                  {(form.coach_ids || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2" data-testid="teams-coach-chips">
                      {(form.coach_ids || []).map(cid => {
                        const u = users.find(x => x.id === cid);
                        if (!u) return null;
                        return (
                          <span key={cid} data-testid={`teams-coach-chip-${cid}`} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs bg-[#83B68B]/15 border border-[#83B68B]/40 text-[#83B68B]">
                            {u.name}
                            <button
                              type="button"
                              onClick={()=>setForm(cur => ({ ...cur, coach_ids: (cur.coach_ids || []).filter(x => x !== cid) }))}
                              data-testid={`teams-coach-chip-remove-${cid}`}
                              className="opacity-70 hover:opacity-100 hover:text-red-400"
                              aria-label={`Remove ${u.name}`}
                            >×</button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                  <div className="relative mt-2">
                    <Input
                      type="text"
                      value={coachSearch}
                      onChange={e => setCoachSearch(e.target.value)}
                      onFocus={()=>setCoachSearchOpen(true)}
                      onBlur={()=>setTimeout(()=>setCoachSearchOpen(false), 150)}
                      placeholder="Type a name to add… e.g. 'Ri' for Richard, Ricky…"
                      data-testid="teams-coach-search"
                    />
                    {coachSearchOpen && (() => {
                      const q = coachSearch.trim().toLowerCase();
                      const results = users
                        .filter(u => u.id !== form.owner_id && !(form.coach_ids || []).includes(u.id))
                        .filter(u => !q || (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q))
                        .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                        .slice(0, 8);
                      if (results.length === 0) return (
                        <div className="absolute z-10 left-0 right-0 mt-1 rounded border border-border bg-popover shadow-lg p-2 text-xs opacity-70" data-testid="teams-coach-search-empty">
                          {q ? `No users match "${coachSearch}"` : "No more users to add"}
                        </div>
                      );
                      return (
                        <div className="absolute z-10 left-0 right-0 mt-1 rounded border border-border bg-popover shadow-lg max-h-52 overflow-y-auto" data-testid="teams-coach-search-results">
                          {results.map(u => (
                            <button
                              key={u.id}
                              type="button"
                              onMouseDown={(e)=>e.preventDefault()}
                              onClick={()=>{
                                setForm(cur => ({ ...cur, coach_ids: [...(cur.coach_ids || []), u.id] }));
                                setCoachSearch("");
                              }}
                              data-testid={`teams-coach-search-pick-${u.id}`}
                              className="w-full text-left text-xs px-3 py-2 hover:bg-muted/60 flex items-center gap-2"
                            >
                              <span className="font-medium">{u.name}</span>
                              <span className="opacity-50">· {u.role}</span>
                              {u.email && <span className="opacity-50 truncate ml-auto">{u.email}</span>}
                            </button>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                  <div className="text-[10px] opacity-50 mt-1.5">{(form.coach_ids || []).length} selected · start typing to find more</div>
                </div>
              )}
              {editingTeam && (
                <div className="col-span-2 pt-3 border-t border-border">
                  <Label className="text-xs uppercase tracking-widest opacity-70">Team crest</Label>
                  <div className="mt-2">
                    <ImageUpload
                      endpoint={`/files/team/${editingTeam.id}`}
                      currentPath={editingTeam.crest_path}
                      onUploaded={(r) => { setEditingTeam({ ...editingTeam, crest_path: r.storage_path }); load(); }}
                      shape="circle"
                      testId="team-crest-upload"
                      label="Upload crest"
                    />
                  </div>
                </div>
              )}
              <div className="col-span-2 dialog-actions">
                <button onClick={submitTeam} data-testid="teams-create-btn" className="btn-volt w-full py-3 rounded-md">{editingTeam ? "Save changes" : "Create team"}</button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {teams.length > 0 && (
        <div className="mb-6 flex flex-wrap gap-3 items-center card-tactical rounded-md p-4" data-testid="teams-filter-bar">
          <div className="text-[10px] tracking-widest uppercase opacity-60 mr-1">Filter</div>
          <Select value={filterAge} onValueChange={setFilterAge}>
            <SelectTrigger className="w-36" data-testid="teams-filter-age"><SelectValue placeholder="Age group" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All age groups</SelectItem>
              {uniqueAges.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterLeague} onValueChange={setFilterLeague}>
            <SelectTrigger className="w-44" data-testid="teams-filter-league"><SelectValue placeholder="League" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All leagues</SelectItem>
              <SelectItem value="none">No league</SelectItem>
              {leagues.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterSquad} onValueChange={setFilterSquad}>
            <SelectTrigger className="w-40" data-testid="teams-filter-squad"><SelectValue placeholder="Squad" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All squads</SelectItem>
              <SelectItem value="none">No squad</SelectItem>
              {uniqueSquads.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterCoach} onValueChange={setFilterCoach}>
            <SelectTrigger className="w-44" data-testid="teams-filter-coach"><SelectValue placeholder="Coach" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All coaches</SelectItem>
              <SelectItem value="none">No coach</SelectItem>
              {uniqueCoaches.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          {anyFilter && (
            <button onClick={clearFilters} className="text-xs tracking-widest uppercase px-3 py-1.5 border border-border hover:border-[#83B68B] hover:text-[#83B68B] rounded inline-flex items-center gap-1.5" data-testid="teams-filter-clear">
              <X className="w-3 h-3" /> Clear
            </button>
          )}
          {mismatchCount > 0 && (
            <>
              <button
                onClick={() => setFilterMismatch(v => !v)}
                data-testid="teams-filter-mismatch"
                className={`text-xs tracking-widest uppercase px-3 py-1.5 rounded inline-flex items-center gap-1.5 border transition-colors ${
                  filterMismatch
                    ? "bg-amber-500/20 border-amber-400 text-amber-200"
                    : "border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                }`}
              >
                <AlertTriangle className="w-3 h-3" /> {mismatchCount} format {mismatchCount === 1 ? "mismatch" : "mismatches"}
              </button>
              {isAdmin && (
                <button
                  onClick={fixAllMismatches}
                  disabled={bulkFixing}
                  data-testid="teams-fix-all-mismatches"
                  className="text-xs tracking-widest uppercase font-bold px-3 py-1.5 rounded inline-flex items-center gap-1.5 border border-amber-400 bg-amber-500/20 text-amber-100 hover:bg-amber-500/30 disabled:opacity-40"
                >
                  <Wrench className="w-3 h-3" /> {bulkFixing ? "Fixing…" : `Fix all ${mismatchCount}`}
                </button>
              )}
            </>
          )}
          <div className="flex-1" />
          <div className="text-xs opacity-60" data-testid="teams-filter-count">{filteredTeams.length} of {teams.length}</div>
        </div>
      )}

      {teams.length === 0 ? (
        <div className="card-tactical rounded-md p-12 text-center">
          <div className="font-display text-2xl">No teams yet</div>
          <p className="opacity-60 mt-2">Create your first squad to attach it to bookings.</p>
        </div>
      ) : filteredTeams.length === 0 ? (
        <div className="card-tactical rounded-md p-12 text-center">
          <div className="font-display text-2xl">No teams match those filters</div>
          <p className="opacity-60 mt-2">Adjust or clear the filters above.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6" data-testid="teams-grid">
          {filteredTeams.map(t => {
            const rule = rulesByAge[(t.age_group || "").toUpperCase()];
            const mismatch = isMismatch(t);
            return (
            <div key={t.id} className={`card-tactical rounded-md p-6 ${mismatch ? "border-amber-500/40" : ""}`} data-testid={`team-card-${t.id}`}>
              <div className="flex justify-between items-start gap-3">
                <div className="flex gap-3 items-start min-w-0">
                  {t.crest_path ? (
                    <StorageImage path={t.crest_path} alt={t.name} className="w-10 h-10 rounded-md object-cover flex-shrink-0 border border-border" />
                  ) : (
                    <div className="w-10 h-10 rounded-md border border-border flex-shrink-0" style={{ background: colorHex(t.color) }} title={kitName(t.color)} />
                  )}
                  <div className="min-w-0">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B]">{t.age_group}{t.game_format ? ` · ${t.game_format}` : ""}{t.gender ? ` · ${genderLabel(t.gender)}` : ""}{t.color ? ` · ${kitName(t.color)}` : ""}</div>
                    <div className="font-display font-black text-2xl tracking-tight mt-1">{t.name}</div>
                    <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs opacity-70 mt-1">
                      {t.squad && <span>{t.squad}</span>}
                      {t.coach_name && <span>Coach · {t.coach_name}</span>}
                      {t.league_name && <span className="text-[#83B68B]">🏆 {t.league_name}</span>}
                      {t.default_venue_id && venueById[t.default_venue_id] && (
                        <span data-testid={`team-default-venue-${t.id}`}>📍 {venueById[t.default_venue_id].name}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  {!isAdmin && t.owner_id !== user?.id && (t.coach_ids || []).includes(user?.id) && (
                    <button onClick={()=>setFtTeam(t)} title="Full-Time administrator" className="opacity-60 hover:opacity-100 hover:text-[#83B68B] p-2" data-testid={`team-ft-admin-${t.id}`}><UserCog className="w-4 h-4" /></button>
                  )}
                  {isAdmin && teams.length > 1 && (
                    <button onClick={()=>setMergeSource(t)} title="Merge into another team" className="opacity-60 hover:opacity-100 hover:text-[#83B68B] p-2" data-testid={`team-merge-${t.id}`}><GitMerge className="w-4 h-4" /></button>
                  )}
                  {(isAdmin || t.owner_id === user?.id) && (
                    <>
                      <button onClick={()=>openEdit(t)} className="opacity-60 hover:opacity-100 hover:text-[#83B68B] p-2" data-testid={`team-edit-${t.id}`}><Pencil className="w-4 h-4" /></button>
                      <button onClick={()=>deleteTeam(t.id)} className="opacity-40 hover:opacity-100 hover:text-red-500 p-2" data-testid={`team-delete-${t.id}`}><Trash2 className="w-4 h-4" /></button>
                    </>
                  )}
                </div>
              </div>
              {mismatch && rule && (
                <div className="mt-3 p-3 rounded border border-amber-500/40 bg-amber-500/10 flex items-center justify-between gap-3" data-testid={`team-mismatch-${t.id}`}>
                  <div className="flex items-start gap-2 min-w-0">
                    <AlertTriangle className="w-4 h-4 text-amber-300 flex-shrink-0 mt-0.5" />
                    <div className="text-xs text-amber-100 min-w-0">
                      Squad set to <strong>{t.game_format}</strong> but Future Fit says <strong>{t.age_group} → {rule.game_format}</strong>.
                    </div>
                  </div>
                  {(isAdmin || t.owner_id === user?.id) && (
                    <button
                      onClick={() => fixFormat(t)}
                      data-testid={`team-fix-format-${t.id}`}
                      className="text-[10px] tracking-widest uppercase font-bold px-2.5 py-1.5 rounded border border-amber-400 text-amber-100 hover:bg-amber-500/20 inline-flex items-center gap-1.5 whitespace-nowrap flex-shrink-0"
                    >
                      <Wrench className="w-3 h-3" /> Fix to {rule.game_format}
                    </button>
                  )}
                </div>
              )}
              {rule && (
                <div className="mt-4 pt-4 border-t border-border grid grid-cols-2 md:grid-cols-4 gap-2" data-testid={`team-fa-rules-${t.id}`}>
                  {[
                    { label: "Format", value: rule.game_format },
                    { label: "Ball", value: rule.ball_size ? `Size ${rule.ball_size}` : null },
                    { label: "Duration", value: rule.duration_min ? `${rule.duration_min}′${rule.halves ? ` · ${rule.halves}` : ""}` : null },
                    { label: "Goal", value: rule.goal_size },
                    { label: "Pitch", value: rule.pitch_size },
                  ].filter(x => x.value).map(x => (
                    <div key={x.label} className="rounded border border-border bg-muted/40 px-2.5 py-1.5" data-testid={`team-rule-${t.id}-${x.label.toLowerCase()}`}>
                      <div className="text-[9px] tracking-widest uppercase opacity-50">{x.label}</div>
                      <div className="text-xs font-mono mt-0.5">{x.value}</div>
                    </div>
                  ))}
                </div>
              )}
              {isAdmin && (
                <div className="mt-4 pt-4 border-t border-border flex items-center gap-2" data-testid={`team-owner-row-${t.id}`}>
                  <UserCog className="w-3.5 h-3.5 opacity-60 flex-shrink-0" />
                  <span className="text-[10px] tracking-widest uppercase opacity-60 whitespace-nowrap">Reassign to</span>
                  <UserPicker users={users} value={t.owner_id || null} noneLabel="— unassigned —" placeholder="— unassigned —"
                    onChange={(v)=>reassignOwner(t.id, v)} className="h-8 text-xs flex-1" testId={`team-owner-select-${t.id}`} />
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}
      <FtAdminDialog team={ftTeam} onClose={() => setFtTeam(null)} onSaved={() => { setFtTeam(null); load(); }} />
      <MergeTeamDialog source={mergeSource} teams={teams} onClose={() => setMergeSource(null)} onMerged={() => { setMergeSource(null); load(); }} />
    </div>
  );
}
